<script setup>
  import { ref, computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import FormDialog from "@/components/episto-ui/form-dialog/FormDialog.vue";
  import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { Label } from "@/components/ui/label";
  import { Separator } from "@/components/ui/separator";
  import { useCampaignsPivotStore } from "@stores/useCampaignsPivotStore";
  import ConditionRows from "./ConditionRows.vue";

  const props = defineProps({
    open: { type: Boolean, default: false },
    group: { type: Object, default: null },
  });
  const emit = defineEmits(["update:open", "save", "delete"]);

  const store = useCampaignsPivotStore();
  const { surveyQuestions, surveyTargets } = storeToRefs(store);

  const tab = ref("quota");
  const name = ref("");
  const targetId = ref(null);
  const conditions = ref([]);

  const targetsWithQuotas = computed(() =>
    surveyTargets.value.filter((t) => t.quotas?.length > 0),
  );

  const hasQuotas = computed(() => targetsWithQuotas.value.length > 0);

  function emptyCondition() {
    return { id: `cond_${Date.now()}`, question_id: null, operator: "and", type: "is", answers: [] };
  }

  // Migrate legacy filters format to conditions array
  function conditionsFromGroup(group) {
    if (group.conditions?.length > 0) {
      return group.conditions.map((c) => ({ id: `cond_${Date.now()}`, ...c }));
    }
    if (group.filters && Object.keys(group.filters).length > 0) {
      return Object.entries(group.filters).map(([qid, answers]) => ({
        ...emptyCondition(),
        question_id: qid,
        answers: Object.keys(answers),
      }));
    }
    return [];
  }

  watch(
    () => props.open,
    (open) => {
      if (!open) return;
      tab.value = !props.group && hasQuotas.value ? "quota" : "custom";
      if (props.group) {
        name.value = props.group.name || "";
        targetId.value = props.group.target_id ? String(props.group.target_id) : null;
        conditions.value = conditionsFromGroup(props.group);
      } else {
        name.value = "";
        targetId.value = null;
        conditions.value = [];
      }
    },
  );

  const targetOptions = computed(() =>
    surveyTargets.value.map((t) => ({ value: String(t.id), label: t.name })),
  );

  // Show internal name (not title) like TargetConditions does
  const questionOptions = computed(() =>
    surveyQuestions.value
      .filter((q) => q.choices && Object.keys(q.choices).length > 0)
      .map((q) => ({ value: String(q.id), label: q.name || q.title })),
  );

  function getValueOptions(cond) {
    const q = surveyQuestions.value.find((sq) => String(sq.id) === String(cond.question_id));
    if (!q?.choices) return [];
    return Object.entries(q.choices).map(([code, c]) => ({
      value: code,
      label: c.title || c.label || code,
    }));
  }

  function addFromQuota(group, answerQuota) {
    store.addGroup({
      name: answerQuota.answer_name || answerQuota.answer,
      target_id: group.survey_target_id,
      conditions: [
        {
          id: `cond_${Date.now()}`,
          question_id: String(group.question_id),
          operator: "and",
          type: "is",
          answers: [answerQuota.answer],
        },
      ],
    });
    emit("update:open", false);
  }

  function handleSave() {
    if (!name.value.trim()) return;
    const activeConditions = conditions.value.filter((c) => c.question_id && c.answers.length > 0);
    emit("save", {
      name: name.value.trim(),
      target_id: targetId.value || null,
      conditions: activeConditions,
    });
    emit("update:open", false);
  }

  function handleDelete() {
    emit("delete");
    emit("update:open", false);
  }
</script>

<template>
  <FormDialog
    :open="open"
    entity-name="Group"
    :is-editing="!!group"
    max-width="560px"
    :can-save="tab === 'custom' && !!name.trim()"
    @update:open="emit('update:open', $event)"
    @save="handleSave">
    <Tabs v-model="tab">
      <TabsList
        v-if="hasQuotas"
        class="w-full">
        <TabsTrigger
          value="quota"
          class="flex-1">
          From quota
        </TabsTrigger>
        <TabsTrigger
          value="custom"
          class="flex-1">
          Custom
        </TabsTrigger>
      </TabsList>

      <!-- Quota tab -->
      <TabsContent
        value="quota"
        class="mt-3">
        <div
          v-if="targetsWithQuotas.length === 0"
          class="text-xs text-muted-foreground py-2">
          No quotas defined for this survey.
        </div>
        <div
          v-else
          class="flex flex-col gap-4">
          <div
            v-for="target in targetsWithQuotas"
            :key="target.id"
            class="flex flex-col gap-2">
            <p class="text-xs font-semibold">{{ target.name }}</p>
            <div
              v-for="quotaGroup in target.quotas"
              :key="quotaGroup.id"
              class="flex flex-col gap-1 pl-2 border-l">
              <p class="text-xs text-muted-foreground">{{ quotaGroup.question_name }}</p>
              <div class="flex flex-wrap gap-1.5">
                <Button
                  v-for="answerQuota in quotaGroup.answer_quotas"
                  :key="answerQuota.id"
                  variant="outline"
                  size="sm"
                  class="h-7 text-xs px-3"
                  @click="addFromQuota(quotaGroup, answerQuota)">
                  {{ answerQuota.answer_name || answerQuota.answer }}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </TabsContent>

      <!-- Custom tab -->
      <TabsContent
        value="custom"
        class="flex flex-col gap-4 mt-3">
        <div class="flex flex-col gap-1.5">
          <Label>Group name</Label>
          <Input
            v-model="name"
            placeholder="e.g. Men 25–34" />
        </div>

        <div class="flex flex-col gap-1.5">
          <Label>
            Target
            <span class="text-muted-foreground font-normal text-xs">(optional)</span>
          </Label>
          <Select
            v-model="targetId"
            :options="targetOptions"
            placeholder="All targets"
            clearable />
        </div>

        <Separator />

        <div class="flex flex-col gap-1.5">
          <Label>Conditions</Label>
          <ConditionRows
            v-model:conditions="conditions"
            :question-options="questionOptions"
            :get-value-options="getValueOptions" />
        </div>
      </TabsContent>
    </Tabs>

    <!-- Delete for edit mode -->
    <div
      v-if="group"
      class="border-t pt-3 -mb-2">
      <Button
        variant="ghost"
        size="sm"
        class="text-destructive hover:text-destructive hover:bg-destructive/10"
        @click="handleDelete">
        Delete group
      </Button>
    </div>
  </FormDialog>
</template>
